import React, { useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldWarning, UploadSimple, Crown, Briefcase, Target, Wrench, CaretDown, Flask } from "@phosphor-icons/react";
import { api } from "../lib/api";
import ThemeSwitcher from "./ThemeSwitcher";
import SpendImporterModal from "./SpendImporterModal";
import { ROLE_REGISTRY, ROLE_BY_ID, useCurrentOperative } from "../lib/rbac";

const ROLE_GLYPH = { DIRECTOR: Crown, MANAGER: Briefcase, CATEGORY_LEADER: Target, BUYER: Wrench };

// Seed-tenant sentinel — every row inserted by /app/backend/seed.py carries
// this tenant_id (see PR-1 multi-tenancy seed sentinel comment in seed.py).
// When the active session is bound to this tenant, the dashboard is rendering
// synthetic demo data, not real customer spend. Surface that loudly so demo
// screenshots can't be mistaken for production figures.
const DEMO_TENANT_ID = "00000000-0000-0000-0000-000000000001";

export default function HeaderBar() {
  const [integrity, setIntegrity] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [isDemoTenant, setIsDemoTenant] = useState(false);
  const { operative, setRole } = useCurrentOperative();

  useEffect(() => {
    api.integrity().then(setIntegrity).catch(() => setIntegrity({ verified: false, passed: 0, total: 11 }));
    api.whoami()
      .then((w) => setIsDemoTenant(w?.tenant_id === DEMO_TENANT_ID))
      .catch(() => setIsDemoTenant(false));
  }, []);

  const verified = integrity?.verified;
  const passed = integrity?.passed ?? 0;
  const total = integrity?.total ?? 11;

  return (
    <header
      data-testid="command-header"
      className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur border-b border-slate-800"
    >
      <div className="px-6 py-4 flex flex-row items-center justify-between gap-6">
        {/* Brand block — PROCUREO stacked over 'your strategic business partner' */}
        <div
          className="flex flex-col shrink-0 whitespace-nowrap"
          data-testid="brand"
        >
          <div
            className="font-serif-brand text-[28px] font-extrabold leading-none whitespace-nowrap"
            style={{ letterSpacing: "0.02em", color: "#0D9488" }}
            data-testid="brand-wordmark"
          >
            PROCUREO
          </div>
          <div
            className="font-inter text-[11px] font-semibold text-slate-300 mt-1 whitespace-nowrap"
            style={{ letterSpacing: "0.08em", color: "#334155" }}
            data-testid="brand-subline"
          >
            Your Strategic Business Partner
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <OperativePill operative={operative} onRole={setRole} />
          {isDemoTenant && <DemoDataBadge />}
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            data-testid="upload-strategic-spend-btn"
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-sm
                       bg-emerald-800 hover:bg-emerald-900 text-white
                       font-mono text-[11px] tracking-mono-label font-bold transition-colors"
            title="Import a CSV / XLSX spend file"
          >
            <UploadSimple weight="bold" size={13} />
            Upload Strategic Spend
          </button>
          <ThemeSwitcher />
          <CfoInsuranceBadge verified={verified} passed={passed} total={total} />
        </div>
      </div>

      <SpendImporterModal
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={() => {
          // After a successful import, re-pull integrity so the badge refreshes
          api.integrity().then(setIntegrity).catch(() => {});
        }}
      />

      {/* Data-fabric ribbon removed — production-clean header */}
    </header>
  );
}

function OperativePill({ operative, onRole }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const role = ROLE_BY_ID[operative.role] || ROLE_REGISTRY[3];
  const Glyph = ROLE_GLYPH[operative.role] || Wrench;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const initials = operative.name.split(/\s+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");

  return (
    <div ref={ref} className="relative" data-testid="acting-operative-pill">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm border border-slate-700 bg-slate-900 hover:border-slate-500 hover:bg-slate-800 transition-colors"
        title="Acting operative · click to switch role"
        data-testid="acting-operative-trigger"
      >
        <span
          aria-hidden
          className="inline-flex items-center justify-center h-6 w-6 rounded-sm font-mono text-[10px] font-bold text-slate-50"
          style={{ background: "#0F172A", border: "1px solid #334155" }}
        >
          {initials}
        </span>
        <span className="hidden sm:flex flex-col items-start leading-tight">
          <span className="font-mono text-[10px] tracking-mono-label text-slate-400 uppercase font-bold flex items-center gap-1">
            <Glyph weight="bold" size={9} /> {role.label}
          </span>
          <span className="font-mono text-[11px] text-slate-100 font-bold tracking-tight">
            {operative.name}
          </span>
        </span>
        <CaretDown weight="bold" size={10} className="text-slate-400" />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-1 w-[280px] rounded-sm border bg-slate-950 border-slate-700 shadow-lg overflow-hidden z-50"
          data-testid="acting-operative-menu"
        >
          <div className="px-3 py-2 border-b border-slate-800">
            <div className="font-mono text-[9px] text-slate-500 tracking-mono-label uppercase font-bold">
              Acting as
            </div>
            <div className="font-display text-[13px] text-slate-100 font-bold tracking-tight">
              {operative.name}
            </div>
            <div className="font-mono text-[10px] text-slate-400">{operative.email}</div>
          </div>
          {ROLE_REGISTRY.map((r) => {
            const G = ROLE_GLYPH[r.id];
            const selected = r.id === operative.role;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => { onRole(r.id); setOpen(false); }}
                data-testid={`role-switch-${r.id.toLowerCase()}`}
                className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors border-b border-slate-800
                            ${selected ? "bg-slate-900" : "hover:bg-slate-900"}`}
              >
                <span
                  className="inline-flex items-center justify-center h-6 w-6 rounded-sm shrink-0"
                  style={{
                    background: selected ? "#0D9488" : "#0F172A",
                    color: "#F9FAFB",
                    border: `1px solid ${selected ? "#0D9488" : "#334155"}`,
                  }}
                >
                  <G weight="bold" size={11} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-mono text-[11px] text-slate-100 font-bold tracking-tight">{r.label}</span>
                  <span className="block font-mono text-[9.5px] text-slate-400 uppercase tracking-mono-label">{r.sub}</span>
                </span>
                {selected && (
                  <span className="font-mono text-[9px] text-emerald-300 font-bold tracking-mono-label">ACTIVE</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function DemoDataBadge() {
  // Visible-but-not-intrusive pill that flags the active dashboard as
  // running on synthetic seed data (see /app/backend/seed.py). Amber tones
  // intentionally chosen to differentiate from the emerald "Verified"
  // integrity badge — operators glancing at the header should instantly
  // see "demo, not real customer spend" even in a screenshot.
  return (
    <div
      data-testid="demo-data-badge"
      title="Active tenant is the seed sentinel. All spend figures, suppliers, POs, and operatives on this dashboard are synthetic fixtures from /app/backend/seed.py, not real customer data."
      className="flex items-center gap-2 px-3 py-1.5 rounded-sm border border-amber-500/40 bg-amber-500/10 shrink-0"
      style={{ letterSpacing: "0.05em" }}
    >
      <Flask weight="fill" className="text-amber-400" size={16} />
      <div className="leading-tight">
        <div
          className="font-mono text-[9px] font-bold text-amber-300 uppercase tracking-mono-label"
        >
          Demo Data
        </div>
        <div className="font-mono text-[10px] text-amber-100">
          synthetic fixtures · not customer spend
        </div>
      </div>
    </div>
  );
}

function CfoInsuranceBadge({ verified, passed, total }) {
  if (verified === null || verified === undefined) {
    return (
      <div className="h-10 w-56 skeleton rounded-sm" data-testid="cfo-badge-loading" />
    );
  }
  if (verified) {
    return (
      <div
        data-testid="cfo-insurance-badge"
        className="verified-pulse flex items-center gap-3 px-4 py-2 rounded-sm border border-emerald-500/40 bg-emerald-500/10 shrink-0"
      >
        <ShieldCheck weight="fill" className="text-emerald-400" size={22} />
        <div className="leading-tight">
          <div className="tracking-mono-label text-[10px] text-emerald-300">
            Data Integrity · Verified
          </div>
          <div className="font-mono text-xs text-emerald-100">
            {passed}/{total} assertions · live ledger reconcile
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      data-testid="cfo-insurance-badge"
      className="flex items-center gap-3 px-4 py-2 rounded-sm border border-amber-500/40 bg-amber-500/10 shrink-0"
    >
      <ShieldWarning weight="fill" className="text-amber-400" size={22} />
      <div className="leading-tight">
        <div className="tracking-mono-label text-[10px] text-amber-300">
          Data Integrity · Drift Detected
        </div>
        <div className="font-mono text-xs text-amber-100">{passed}/{total} assertions passing</div>
      </div>
    </div>
  );
}
